// components/AppInitializer.tsx
import React from 'react';
import { useAuthStore } from '../../store/authStore.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';

/**
 * Bootstraps auth state at app startup.
 *
 * Mount this once, right inside your QueryClientProvider and
 * outside your Router so it runs before any route renders.
 *
 * It calls useCurrentUser() which:
 *   1. Checks if a token exists in the store
 *   2. If yes, fetches /api/users/me/ and syncs it into Zustand
 *   3. Shows a loading screen until the first fetch completes
 */
const AppInitializer = ({ children }: { children: React.ReactNode }) => {
  const { isLoading: storeLoading, token } = useAuthStore();
  const { isLoading: queryLoading } = useCurrentUser();

  // Only block rendering during the very first load when a token exists.
  // If there's no token, we know immediately the user is logged out.
  const showLoader = token && (storeLoading || queryLoading);

  if (showLoader) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          {/* Replace with your app's actual spinner/logo */}
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading your profile…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AppInitializer;